import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@swyftx/aviary/atoms/Card';
import { Input } from '@swyftx/aviary/atoms/Input';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Tooltip } from '@swyftx/aviary/atoms/Tooltip/Tooltip';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { ArrowChevronDown, ArrowChevronUp } from '@swyftx/aviary/icons/outlined';
import { Menu, MenuProps } from '@swyftx/aviary/molecules/Menu';

import { cn } from '@shared/utils/lib/ui';

import { useMarginInstruments } from 'src/lib/margin/hooks/instruments';
import { AssetIconsGroup } from 'src/lib/markets/components';

import { MarginInstrumentTable } from '../MarginInstrumentsTable/MarginInstrumentTable';

type Props = {
  className?: string;
  variant?: MenuProps['variant'];
};

const MarginInstrumentSelector: React.FC<Props> = ({ className, variant = 'standard' }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const { selectedMarketPrimary, selectedMarketSecondary } = useMarginInstruments();
  const { t } = useTranslation('margin');

  const assets = useMemo(() => {
    if (!selectedMarketPrimary || !selectedMarketSecondary) return [];

    return [selectedMarketSecondary, selectedMarketPrimary];
  }, [selectedMarketPrimary, selectedMarketSecondary]);

  const onSelectItem = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, []);

  return (
    <Menu
      variant={variant}
      open={open}
      title={t('instruments.selector.title')}
      onOpenChange={setOpen}
      sideOffset={0}
      padding={false}
      className='w-[840px] overflow-hidden'
      modalClassName='min-h-[90dvh] h-[90dvh] z-modal-popover'
      align='start'
      triggerContent={
        <div data-testid='margin-instrument-selector-trigger'>
          <Tooltip title={t('instruments.selector.tooltip')} align='center' side='bottom'>
            <Card
              className={cn(
                className,
                'cursor-pointer rounded-[24px] p-8',
                open ? 'border-color-border-info bg-color-background-info-subtle' : 'hover:border-color-border-hover',
              )}
            >
              <FlexLayout alignItems='center' spacing={12}>
                <AssetIconsGroup assets={assets} size={24} />
                <Body weight='emphasis' size='large'>
                  {selectedMarketSecondary?.code}
                  {selectedMarketPrimary?.code}
                </Body>
                {open ? (
                  <ArrowChevronUp className='h-20 w-20 text-color-text-primary' />
                ) : (
                  <ArrowChevronDown className='h-20 w-20 text-color-text-primary' />
                )}
              </FlexLayout>
            </Card>
          </Tooltip>
        </div>
      }
    >
      <FlexLayout direction='column' spacing={0}>
        <FlexLayout
          direction='column'
          spacing={12}
          className='sticky top-0 z-10 border-b border-color-border-main bg-color-background-surface-primary px-12 py-16'
        >
          <Input
            placeholder={t('instruments.selector.placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch('')}
          />
        </FlexLayout>
        <MarginInstrumentTable search={search} onSelectItem={onSelectItem} />
      </FlexLayout>
    </Menu>
  );
};

export { MarginInstrumentSelector };
